import { Link } from "react-router-dom";
import { Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { QuantityStepper } from "../components/QuantityStepper";

export default function Cart() {
  const { cart, loading, updateQty, removeItem } = useCart();

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center label-overline text-jow-navy/55" data-testid="cart-loading">
        Loading cart…
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="cart-empty">
        <span className="label-overline text-jow-gold">Empty cart</span>
        <h1 className="font-display text-4xl md:text-5xl mt-3 text-jow-navy">
          Nothing here yet.
        </h1>
        <p className="mt-3 text-jow-navy/60">
          Visit the atelier and pick a piece you love.
        </p>
        <Link to="/shop" className="btn-primary-jow mt-10 inline-flex" data-testid="cart-empty-cta">
          <ShoppingBag size={14} /> Browse the atelier
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20" data-testid="cart-page">
      <div className="mb-10">
        <span className="label-overline text-jow-gold">Your cart</span>
        <h1 className="font-display text-4xl md:text-5xl mt-2 text-jow-navy">
          {cart.count} {cart.count === 1 ? "piece" : "pieces"} in the bag.
        </h1>
      </div>

      <div className="grid lg:grid-cols-12 gap-10">
        <ul className="lg:col-span-8 space-y-5" data-testid="cart-items">
          {cart.items.map((it) => (
            <li
              key={it.sku}
              className="bg-white border border-jow-navy/10 p-4 md:p-5 flex flex-col sm:flex-row gap-5"
              data-testid={`cart-item-${it.sku}`}
            >
              <Link to={`/product/${it.slug}`} className="shrink-0">
                <img
                  src={it.image}
                  alt={it.name}
                  className="w-28 h-28 sm:w-32 sm:h-32 object-cover"
                />
              </Link>
              <div className="flex-1 flex flex-col gap-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Link to={`/product/${it.slug}`}>
                      <h3 className="font-display text-xl text-jow-navy leading-tight">
                        {it.name}
                      </h3>
                    </Link>
                    <p className="text-xs text-jow-navy/55 mt-1">
                      SKU {it.sku} · {it.attrs?.color}
                      {it.attrs?.size && it.attrs.size !== "Free Size" ? ` · ${it.attrs.size}` : ""}
                    </p>
                  </div>
                  <button
                    onClick={() => removeItem(it.sku)}
                    className="text-jow-navy/50 hover:text-jow-pink p-2"
                    data-testid={`cart-remove-${it.sku}`}
                    aria-label="Remove"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="flex items-end justify-between">
                  <QuantityStepper
                    value={it.qty}
                    min={0}
                    max={Math.max(1, it.available)}
                    onChange={(q) => updateQty(it.sku, q)}
                    testid={`cart-qty-${it.sku}`}
                  />
                  <span className="font-display text-xl text-jow-navy">
                    ₹{it.line_total.toLocaleString("en-IN")}
                  </span>
                </div>
              </div>
            </li>
          ))}
        </ul>

        <aside
          className="lg:col-span-4 bg-jow-cream border border-jow-gold/40 p-6 md:p-8 self-start sticky top-24"
          data-testid="cart-summary"
        >
          <p className="label-overline text-jow-gold">Order summary</p>
          <div className="mt-5 space-y-3 text-sm">
            <Row label="Subtotal" value={`₹${cart.subtotal.toLocaleString("en-IN")}`} testid="summary-subtotal" />
            <Row
              label={`Shipping${cart.shipping === 0 ? " · free over ₹999" : ""}`}
              value={cart.shipping === 0 ? "Free" : `₹${cart.shipping}`}
              testid="summary-shipping"
            />
            <div className="border-t border-jow-navy/10 pt-3 mt-3">
              <Row
                label={<span className="font-display text-lg">Total</span>}
                value={
                  <span className="font-display text-lg text-jow-navy">
                    ₹{cart.total.toLocaleString("en-IN")}
                  </span>
                }
                testid="summary-total"
              />
            </div>
          </div>

          <div className="mt-6">
            <p className="label-overline text-jow-navy/55 mb-2">Coupon</p>
            <p className="text-xs text-jow-navy/55">
              Apply your coupon at checkout — try{" "}
              <code className="text-jow-pink">WELCOME10</code>,{" "}
              <code className="text-jow-pink">FESTIVE500</code>, or{" "}
              <code className="text-jow-pink">RIKSHAW100</code>.
            </p>
          </div>

          <Link
            to="/checkout"
            className="btn-primary-jow w-full mt-6"
            data-testid="checkout-cta"
          >
            Proceed to Checkout
          </Link>
        </aside>
      </div>
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span className="text-jow-navy/65">{label}</span>
      <span className="text-jow-navy">{value}</span>
    </div>
  );
}
